import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { type SkipRange, getSkipRanges } from '../lib/api';

/**
 * Pomijanie openingu i endingu.
 *
 * Zakresy przychodzą z serwera — u części materiału podaje je host razem
 * z odcinkiem, więc nie ma potrzeby odpytywania AniSkip.
 *
 * Świadomie **bez automatycznego przeskoku domyślnie**. Automat wygląda dobrze
 * w opisie, a w praktyce zabiera wybór: opening bywa tym, po co ktoś włączył
 * odcinek, a przy błędnym zakresie automat przeskakuje treść i nie da się tego
 * cofnąć inaczej niż przewijaniem wstecz. Pokazujemy przycisk; automat jest
 * możliwością, nie zachowaniem.
 */

/** Jak często sprawdzać pozycję. Ćwierć sekundy wystarcza, a nie obciąża. */
const TICK_MS = 250;

/**
 * Nie proponujemy pominięcia w ostatnich sekundach zakresu — przeskok o dwie
 * sekundy jest bardziej irytujący niż jego brak.
 */
const MIN_REMAINING_SEC = 3;

/** Jak długo widać propozycję cofnięcia automatycznego przeskoku. */
const UNDO_WINDOW_MS = 8000;

export interface SkipState {
  ranges: SkipRange[];
  /** Zakres, w którym właśnie jesteśmy, albo `null`. */
  current: SkipRange | null;
  /** Przeskakuje na koniec bieżącego zakresu. */
  skip(): void;
  /**
   * Zakres pominięty automatycznie, dopóki da się to cofnąć.
   *
   * Automat bez tego byłby nieodwracalny inaczej niż przewijaniem na oko —
   * a opening bywa tym, po co ktoś włączył odcinek.
   */
  autoSkipped: { kind: SkipRange['kind']; from: number } | null;
  undoAutoSkip(): void;
}

export function useSkipRanges({
  videoRef,
  titleId,
  episodeNumber,
  durationSec,
  autoSkip = false,
  ready,
}: {
  videoRef: RefObject<HTMLVideoElement | null>;
  titleId: string | null;
  episodeNumber: number | null;
  /**
   * Czas trwania odcinka, jeśli znany.
   *
   * Podajemy go serwerowi, bo AniSkip odrzuca po nim zgłoszenia dotyczące innego
   * wydania (wersja bez cenzury, inne cięcie). Bez tej liczby zakres wychodzący za
   * koniec odcinka przeszedłby, a przeskok na jego koniec zakończyłby odtwarzanie.
   */
  durationSec: number | null;
  /**
   * Pomijaj sam, bez klikania. Domyślnie wyłączone i to jest decyzja, nie
   * ostrożność: przy błędnym zakresie automat przeskakuje treść, a widz nie wie,
   * co stracił.
   */
  autoSkip?: boolean;
  ready: boolean;
}): SkipState {
  /**
   * `NaN` normalizujemy do `null` przy wejściu.
   *
   * `video.duration` jest `NaN` przed wczytaniem metadanych, a `NaN` w tablicy
   * zależności efektu znaczy odpytanie serwera przy każdym renderze — bo
   * `NaN !== NaN`.
   */
  const length =
    typeof durationSec === 'number' && Number.isFinite(durationSec) ? durationSec : null;

  const [ranges, setRanges] = useState<SkipRange[]>([]);
  const [current, setCurrent] = useState<SkipRange | null>(null);

  /**
   * Zakresy w ref, bo takt czyta je co ćwierć sekundy.
   *
   * Trzymanie ich tylko w zależnościach efektu przebudowywałoby interwał przy
   * każdej zmianie listy — ten sam błąd, który raz już wyszedł w takcie
   * synchronizacji pokoju i gubił korekty.
   */
  const rangesRef = useRef<SkipRange[]>([]);

  /** Co już pominął automat w tym odcinku — żeby nie pomijał tego drugi raz. */
  const autoSkippedKinds = useRef<Set<string>>(new Set());
  const [autoSkipped, setAutoSkipped] = useState<{
    kind: SkipRange['kind'];
    from: number;
  } | null>(null);

  useEffect(() => {
    setRanges([]);
    setCurrent(null);
    rangesRef.current = [];
    // Nowy odcinek to nowe zakresy: automat ma prawo pominąć w nim opening raz.
    autoSkippedKinds.current = new Set();
    setAutoSkipped(null);
    if (!ready || titleId === null || episodeNumber === null) return;

    let cancelled = false;
    getSkipRanges(titleId, episodeNumber, length)
      .then((data) => {
        if (cancelled) return;
        /**
         * Jeden zakres na rodzaj. Serwer sortuje po priorytecie źródła
         * (poprawka człowieka przed danymi hosta), więc bierzemy pierwszy
         * napotkany — dwa zakresy openingu z różnych źródeł dawałyby dwa
         * przyciski mówiące to samo.
         */
        const byKind = new Map<string, SkipRange>();
        for (const range of data.ranges) {
          if (!byKind.has(range.kind)) byKind.set(range.kind, range);
        }
        const unique = [...byKind.values()];
        setRanges(unique);
        rangesRef.current = unique;
      })
      .catch(() => {
        // Brak zakresów to normalny stan — większość hostów ich nie podaje.
        if (!cancelled) setRanges([]);
      });

    return () => {
      cancelled = true;
    };
  }, [titleId, episodeNumber, ready, length]);

  useEffect(() => {
    if (!ready) return;
    const timer = setInterval(() => {
      const video = videoRef.current;
      if (video === null) return;
      const t = video.currentTime;
      const hit =
        rangesRef.current.find(
          (range) => t >= range.startSec && t < range.endSec - MIN_REMAINING_SEC,
        ) ?? null;
      // Ustawiamy przez porównanie rodzaju, nie referencji: identyczna wartość
      // nie może wywoływać przerysowania cztery razy na sekundę.
      setCurrent((prev) => (prev?.kind === hit?.kind ? prev : hit));
    }, TICK_MS);
    return () => clearInterval(timer);
  }, [ready, videoRef]);

  const skip = useCallback(() => {
    const video = videoRef.current;
    if (video === null || current === null) return;
    video.currentTime = current.endSec;
    // Zerujemy od razu, żeby przycisk zniknął bez czekania na kolejny takt.
    setCurrent(null);
  }, [current, videoRef]);

  /**
   * Automat: pomijamy raz na zakres i **zapamiętujemy, skąd**.
   *
   * „Raz na zakres" pilnuje `autoSkippedKinds`: bez tego cofnięcie przeskoku
   * wracałoby w ten sam zakres, automat pomijałby go ponownie i widz nie miałby jak
   * obejrzeć openingu. Zbiór zeruje się przy zmianie odcinka, razem z zakresami.
   */
  useEffect(() => {
    if (!autoSkip || current === null) return;
    if (autoSkippedKinds.current.has(current.kind)) return;

    const video = videoRef.current;
    if (video === null) return;

    autoSkippedKinds.current.add(current.kind);
    setAutoSkipped({ kind: current.kind, from: video.currentTime });
    video.currentTime = current.endSec;
    setCurrent(null);
  }, [autoSkip, current, videoRef]);

  /** Po tym czasie propozycja cofnięcia znika — dłużej byłaby śmieciem na obrazie. */
  useEffect(() => {
    if (autoSkipped === null) return;
    const timer = setTimeout(() => setAutoSkipped(null), UNDO_WINDOW_MS);
    return () => clearTimeout(timer);
  }, [autoSkipped]);

  const undoAutoSkip = useCallback(() => {
    const video = videoRef.current;
    if (video === null || autoSkipped === null) return;
    video.currentTime = autoSkipped.from;
    setAutoSkipped(null);
  }, [autoSkipped, videoRef]);

  return { ranges, current, skip, autoSkipped, undoAutoSkip };
}

/** Nazwa rodzaju zakresu dla przycisku. */
export function skipLabel(kind: SkipRange['kind']): string {
  switch (kind) {
    case 'op':
      return 'Pomiń czołówkę';
    case 'ed':
      return 'Pomiń tyłówkę';
    case 'recap':
      return 'Pomiń streszczenie';
    case 'preview':
      return 'Pomiń zapowiedź';
  }
}
